import { inject, Injectable } from '@angular/core';
import { from } from 'rxjs';
import { CustomerEntity } from '../entities/customer.entity';
import { CustomerDataService } from './customer-data.service';
import { PaginatedItemsService, PaginatedItemsState, PaginationDetails } from './paginated-items-service';


export interface customerBackendFilter {
    partnerOfficeIds?: number[];
    customerSpecificationTypeIds?: number[];
    onlyCustomers?: boolean;
    onlySuppliers?: boolean;
}


export interface customerBackendSort {
    property: CustomerSortProperty;
    direction: SortDirection;
}

export enum CustomerSortProperty {
    UpdatedAt = 0,
    CustomerNumber = 1,
    CustomerName = 2,
    Location = 3,
    PostalCode = 4,
}

export enum SortDirection {
    asc = 0,
    desc = 1,
}

const initialState: PaginatedItemsState<CustomerEntity, customerBackendFilter, customerBackendSort> = {
    paginationDetails: {
        offset: 0,
        pageSize: 50,
    },
    pageContent: {
        items: [],
        length: null,
    },
    status: {
        isLoading: false,
        hasError: false,
        isCountLimitReached: false,
    },
};


@Injectable()
export class PaginatedCustomerService extends PaginatedItemsService<CustomerEntity, customerBackendFilter, customerBackendSort> {
    readonly vm$ = this.selectItems$;
    private readonly _customerDataService = inject(CustomerDataService);

    /** Cursor pro bereits erreichter Seite. Index 0 braucht keinen LastId. */
    private pageLastIds: (number | undefined)[] = [undefined];
    private currentSearchQuery = '';

    constructor() {
        super(initialState);
    }

    private currentSort: customerBackendSort = {
        property: CustomerSortProperty.UpdatedAt,
        direction: SortDirection.desc,
    };

    protected getItems({ offset, pageSize, searchQuery, filterObject, sortProperty }: PaginationDetails<customerBackendFilter, customerBackendSort>) {
        return from(this.fetchPage(offset, pageSize, searchQuery, filterObject, sortProperty));
    }



    private async fetchPage(offset: number, pageSize: number, searchQuery: string, filterObject?: customerBackendFilter, sort?: customerBackendSort) {
        const requestedPageIndex = pageSize > 0 ? Math.round(offset / pageSize) : 0;

        if ((searchQuery ?? '') !== this.currentSearchQuery) {
            this.currentSearchQuery = searchQuery ?? '';
            this.pageLastIds = [undefined];
        }

        // Cursor für die gewünschte Seite (noch) nicht bekannt (z.B. Sprung auf "letzte Seite")
        // -> auf Startseite zurückfallen, statt einen falschen/leeren Request zu schicken.
        const pageIndex = requestedPageIndex > 0 && this.pageLastIds[requestedPageIndex] === undefined ? 0 : requestedPageIndex;
        const lastId = this.pageLastIds[pageIndex];

        const { customers, nextLastId, pageCount } =
            await this._customerDataService.fetchCustomersPage(
                this.currentSearchQuery,
                pageSize,
                lastId,
                filterObject,
                sort ?? this.currentSort,
            );

        if (nextLastId !== undefined) {
            this.pageLastIds[pageIndex + 1] = nextLastId;
        }

        const items = customers;

        return {
            items,
            // PageCount ist die Anzahl Seiten (nicht Einträge) - für den MatPaginator hochgerechnet.
            length: pageCount != null ? pageCount * pageSize : null,
            hasError: false,
            isCountLimitReached: false,
        };
    }







    reset() {
        this.pageLastIds = [undefined];
        this.currentSearchQuery = '';
    }
}
